<template>
  <div class="h5-offer-view">
    <div v-if="!showSkeleton">
      <div class="h5-offer-header">
        <H5Carousel
          v-if="detailInfo"
          :data="detailInfo"
          :list="skuImages"
          :desc-images="descriptionImages"
        />
        <Dropshipping :moq="detailInfo?.minOrderQuantity" />
        <PriceRange
          :list="priceRangeList"
          :total="addedSkuTotal"
        />
        <Description
          :detail-info="detailInfo"
        />
      </div>

      <VariantsAndQuantity
        @click="handleH5SourcingPopup"
        :sku-images="skuImages"
        :attributes-list="skuAttributes"
        :min-delivery-time="minDeliveryTime"
        :max-delivery-time="maxDeliveryTime"
      />

      <Specifications
        v-if="detailInfo"
        @show-all="handleH5DescriptionPopup"
        :list="descAllList"
      />

      <FactoryCard
        v-if="detailInfo"
        :factory-id="detailInfo?.sellerOpenId"
      />

      <FactoryRecommend
        v-if="detailInfo"
        :factory-id="detailInfo.sellerOpenId"
      />

      <!-- product details -->
      <Details
        v-if="detailInfo"
        :detail-info="detailInfo"
        :product-images="productImages"
      />

      <AdaptiveIntercomFooter
        class="h5-offer-fixed-footer"
        v-if="!showSkeleton"
      >
        <Footer
          @add-cart="handleH5SourcingPopup"
          :favorite="detailInfo?.collect"
        />
      </AdaptiveIntercomFooter>
    </div>

    <!-- 骨架屏 -->
    <H5EmptyInitSkeleton
      v-if="showSkeleton"
    />
    <H5DescriptionDialog
      v-model:show="showH5DescriptionPopup"
      :desc-all-list="descAllList"
      :close-h5-description="closeH5Description"
    />
    <H5PopupDetail
      v-model="showH5PopupDetail"
      :data="detailInfo"
      :desc-images="descriptionImages"
    />
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router'
import H5Carousel from './components/H5Carousel/index.vue'
import H5DescriptionDialog from './components/H5DescriptionDialog/index.vue'
import H5EmptyInitSkeleton from './components/H5EmptyInitSkeleton/index.vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { buriedPointUtil, gaAnalytics, waitForIntercom } from '@/utils/utils'
import AdaptiveIntercomFooter from '@/components/AdaptiveIntercomFooter/index.vue'
import Description from './components/Description.vue'
import PriceRange from './components/PriceRange.vue'
import { useProductDetail } from '../hooks/useProductDetail'
import Specifications from './components/Specifications.vue'
import VariantsAndQuantity from './components/VariantsAndQuantity.vue'
import H5PopupDetail from './components/H5PopupDetail/index.vue'
import Footer from './components/Footer.vue'
import FactoryCard from './components/FactoryCard.vue'
import FactoryRecommend from './components/FactoryRecommend.vue'
import { useCurrencyStore } from '@/pinia/currency'
import Details from './components/Details.vue'
import Dropshipping from './components/Dropshipping.vue'

const {
  skuAttributes,
  detailInfo,
  showSkeleton,
  productImages,
  descriptionImages,
  formatImageList,
  formatPriceRangeList,
  getDetailInfo,
  getSpecifications
} = useProductDetail()

const route = useRoute()

const currencyStore = useCurrencyStore()

const offerId = route.params.id

const descAllList = ref([])
const showH5DescriptionPopup = ref(false)
const showH5PopupDetail = ref(false)

const priceRangeList = ref([])
const addedSkuTotal = ref(0)
const skuImages = ref([])
const minDeliveryTime = ref(0)
const maxDeliveryTime = ref(0)

const currencyData = computed(() => currencyStore.current)

watch(currencyData, () => {
  if (detailInfo.value) {
    getPriceRangeList()
  }
})

const fetchDetailInfo = async () => {
  await getDetailInfo()
  skuImages.value = formatImageList(detailInfo.value)
  descAllList.value = getSpecifications(detailInfo.value)

  getPriceRangeList()
  document.title = detailInfo.value.subjectTrans
}

const getPriceRangeList = async () => {
  const {
    priceRangeList: list,
    minDeliveryTime: min,
    maxDeliveryTime: max
  } = await formatPriceRangeList(offerId, skuImages.value[0], detailInfo.value.categoryId)
  priceRangeList.value = list
  minDeliveryTime.value = min
  maxDeliveryTime.value = max
}

const showMiniHeader = ref(false)
let lastScrollTop = 0
const handleScroll = () => {
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop

  if (scrollTop > 314 && scrollTop > lastScrollTop) {
    // 向下滚动，显示小头部
    showMiniHeader.value = true
  } else if (scrollTop < lastScrollTop) {
    // 向上滚动，显示大头部
    showMiniHeader.value = false
  }
  lastScrollTop = scrollTop
}

const buriedEmail = () => {
  const { emailType = '', email_id = '', orderNo = '' } = route.query
  if (emailType) {
    buriedPointUtil({ eventId: 'MPPD501', emailId: atob(email_id) })
    !!orderNo && waitForIntercom(orderNo)
  }
}

const handleH5DescriptionPopup = () => {
  showH5DescriptionPopup.value = true
}

const closeH5Description = () => {
  showH5DescriptionPopup.value = false
}

const handleH5SourcingPopup = () => {
  showH5PopupDetail.value = true
}

onBeforeMount(() => {
  gaAnalytics('mp_product_detail_page_view', 'mp_product_detail_page_view', {
    spuId: offerId
  })
  buriedPointUtil({ eventId: 'MPEX101', others: { offerIdList: [offerId] } })
  fetchDetailInfo()
  buriedEmail()
})

onMounted(async () => {
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})

</script>

<style lang="less" scoped>
.h5-offer-view {
  background-color: #F5F5F5;
  overflow-x: hidden;

  .main-header {
    transition: transform .3s ease;
    background-color: #052784;

    &.hidden {
      transform: translateY(-100%);
    }
  }

  .h5-offer-header {
    background-color: #FFFFFF;
    padding-bottom: 12px;
  }

  .h5-offer-fixed-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    backdrop-filter: blur(10px);
    height: 72px;
    border-top: 1px solid #EBF1F5;
    box-sizing: border-box;
    z-index: 99;
  }
}

.image-preview-button {
  position: fixed;
  right: 16px;
  bottom: 34px;
  left: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  background: #F57231;

  &__icon {
    font-size: 24px;
  }
}

</style>
